import React, { FC } from 'react';
import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import ExamQuestionView from './ExamQuestion';
import { AnswerMap, ExamQuestion, hasSelection } from './examState';

/**
 * The whole paper on one sheet, for papers set to that mode.
 *
 * There is no navigator here: every question is already on the page, so moving
 * around it is scrolling. Everything else is unchanged, including the clock,
 * the warnings, autosave and submitting.
 */
export const SingleSheetPaper: FC<{
  questions: ExamQuestion[];
  answers: AnswerMap;
  disabled?: boolean;
  onChange: (questionId: string, value: string[]) => void;
}> = ({ questions, answers, disabled, onChange }) => (
  <Stack spacing={2}>
    {questions.map((question, index) => {
      const answered = hasSelection(answers[question.questionId]);

      return (
        <Card
          key={question.questionId}
          id={`cba-question-${index + 1}`}
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: '0 2px 12px #0000001A',
            // A faint edge on the unanswered ones, so scrolling back for them
            // does not mean rereading every question.
            borderLeft: '4px solid',
            borderLeftColor: answered ? '#0F5C55' : '#D3DAD5',
          }}>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
            <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>
              Question {index + 1} of {questions.length}
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {question.marks} mark{question.marks === 1 ? '' : 's'}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {!answered && <Chip size='small' label='Not answered' variant='outlined' />}
          </Stack>

          <ExamQuestionView
            question={question}
            value={answers[question.questionId] ?? []}
            disabled={disabled}
            onChange={(value) => onChange(question.questionId, value)}
          />
        </Card>
      );
    })}
  </Stack>
);

export default SingleSheetPaper;
